import { index, jsonb, pgTable, timestamp, uniqueIndex, uuid, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// THE MICRO-LOOP SPINE + THE KEYSTONE HUB — Phase 1 of the 2026-07-08 fix
// architecture (docs/design/fix-architecture-2026-07-08/{FIX-ARCHITECTURE.md,
// BUILD-PLAN.md}, Class 6). SQL twin: docs/design/phase1-spine-neon.sql.
//
// L-stamp (provenance jsonb on resumes/cover_letters/presence_profiles) is
// ALREADY LIVE — see packages/schema/src/tailoring.ts + presence_profiles.ts.
// This file is L-decide's data home (loop_acts) and the NEW keystone hub
// (corpus_coverage_state) that D16's ruling adds on top of R130's design.
// =============================================================================

/** The user's verdict on an offered artifact regeneration. Append-only,
 *  terminal per (lineageId, epoch) — a dismiss this epoch is never re-offered
 *  until the epoch moves again. Mirrors SectionRefineDO's /act vocabulary
 *  (accept/close), with 'dismiss' added for the pull-lane's own HOLD-once
 *  semantics (evaluateRegen's 'dismissed' verdict). */
export type LoopActAction = 'accept' | 'dismiss' | 'close';

/** Which artifact family a loop_acts row or offer is about. */
export type LoopArtifactKind = 'resume' | 'cover_letter' | 'presence_profile';

export const loopActs = pgTable(
  'loop_acts',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    artifactKind: varchar('artifact_kind', { length: 24 }).notNull(),
    // Soft pointer (no FK): resumes/cover_letters/presence_profiles are
    // disjoint tables, a single FK can't span them, and artifact rows are
    // immutable (never re-homed) so an orphan risk never arises.
    artifactId: uuid('artifact_id').notNull(),
    // Threads regen versions of "the same offer lineage" — dismiss is
    // terminal per (lineageId, epoch), never per artifactId alone, so a
    // fresh version in the same lineage gets its own chance to be offered.
    lineageId: uuid('lineage_id').notNull(),
    // The corpusEpoch (lib/corpusEpoch.ts) string counter this act was taken
    // against — the terminal-per-epoch key.
    epoch: varchar('epoch', { length: 32 }).notNull(),
    action: varchar('action', { length: 16 }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('loop_acts_user_idx').on(t.userId),
    lineageEpochIdx: index('loop_acts_lineage_epoch_idx').on(t.lineageId, t.epoch),
    artifactIdx: index('loop_acts_artifact_idx').on(t.artifactKind, t.artifactId),
  }),
);

/** The hub's compiled verdict for one (user, topic) — generalizes
 *  SectionRefineDO's terminal status from section-grain to topic-grain
 *  (the DO itself stays byte-untouched; this is a second, parallel compiled
 *  read model a caller-side reader assembles alongside it). */
export type CoverageStatus = 'covered' | 'settled' | 'open_thin';

/** Discriminated topic-key family this hub row compiles state for. Data, not
 *  a schema fork: `topicKey` is always `"${CoverageTopicKind}|${id}"`. */
export type CoverageTopicKind = 'role' | 'axis' | 'section' | 'era';

/** Grounded, structured evidence ONLY — which facets/eras contributed, which
 *  points are strong vs. worth strengthening next. NEVER a generated
 *  sentence: the offer-surface unit and Mira's question-picker read this to
 *  build their OWN grounded sentence at render/prompt time (NO INVENTION —
 *  this column is countable arithmetic, the same law GET /api/loop/offers'
 *  depthDelta already holds). */
export interface CoverageAlignmentNote {
  strongPoints?: string[];
  strengthenNext?: string[];
  contributingFacetIds?: string[];
}

export const corpusCoverageState = pgTable(
  'corpus_coverage_state',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    topicKey: varchar('topic_key', { length: 160 }).notNull(),
    status: varchar('status', { length: 16 }).notNull(),
    alignmentNote: jsonb('alignment_note').$type<CoverageAlignmentNote>(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userTopicIdx: uniqueIndex('corpus_coverage_state_user_topic_idx').on(t.userId, t.topicKey),
  }),
);
